"use client";

import { useMemo } from "react";
import { useWindowSize } from "@/hooks/useWindowSize";
import { mod } from "@/lib/utils";
import { HeroCard } from "./HeroCard";
import type { Collection } from "@/types";

interface HeroCardsProps {
  collections: Collection[];
  activeIndex: number;
  onSelect: (index: number) => void;
}

// Matches --breakpoint-xs/sm/md/lg/xl/2xl (375/640/768/1024/1280/1536)
function getCardWidth(width: number): number {
  if (width === 0) return 190; // SSR fallback before we know real width
  if (width < 375) return 84;
  if (width < 640) return 102;
  if (width < 768) return 128;
  if (width < 1024) return 148;
  if (width < 1280) return 162;
  if (width < 1536) return 178;
  return 196;
}

function getSideCount(width: number): number {
  if (width === 0) return 2;
  if (width < 640) return 1; // phones: center + 1 neighbour, keeps it uncluttered
  return width < 1024 ? 1 : 2; // tablet: 1 side, desktop: 2 sides
}

export function HeroCards({
  collections,
  activeIndex,
  onSelect,
}: HeroCardsProps) {
  const { width } = useWindowSize();
  const cardWidth = getCardWidth(width);
  const side = getSideCount(width);
  const gap = cardWidth * 0.62;
  const total = collections.length;

  const offsets = useMemo(() => {
    const arr: number[] = [];
    for (let o = -side; o <= side; o++) arr.push(o);
    return arr;
  }, [side]);

  return (
    <div
      className="relative w-full"
      style={{ height: `clamp(190px, 34vw, 340px)` }}
      aria-roledescription="carousel"
      aria-label="Collection cards"
    >
      {offsets.map((offset) => {
        const idx = mod(activeIndex + offset, total);
        const collection = collections[idx];
        if (!collection) return null;

        return (
          <HeroCard
            key={`${collection.id}-${offset}`}
            collection={collection}
            index={idx}
            offset={offset}
            cardWidth={cardWidth}
            gap={gap}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}